/**
 * `useOtherRecordTypeFieldCount` — how many fields for the relationships THIS
 * record carries were defined on the other record type instead.
 *
 * Its OWN module, not a third export of `useCustomFieldDefinitions`, for two
 * reasons. It is a derived question rather than a query wrapper: it reads a
 * shared cache entry and answers with a number. And sixteen specs mock that
 * module by listing its exports, so a hook added there breaks every one of them
 * the moment a shared component calls it — which is exactly what happened.
 *
 * The answer matters because it is the one custom-field failure with no symptom
 * at all: a record whose own type defines nothing renders no custom-fields card,
 * so an admin who scoped an Organization field to "Interior Designer" and then
 * opened a CONTACT carrying it sees no mention of custom fields anywhere.
 *
 * See docs/platform/custom-fields-design.md § Phase 2.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listDefinitionsApiV1CustomFieldsDefinitionsGet } from '../../../generated/api/custom-fields/custom-fields';
import {
  RELATIONSHIP_SCOPED_ENTITIES,
  allCustomFieldDefinitionsKey,
  otherRecordTypeFieldCount,
} from './customFieldUtils';

/**
 * Both the card gate (`useCustomFieldsAutoSave`) and the sentence
 * (`HiddenCustomFieldsHint`) read it; they share `allCustomFieldDefinitionsKey`,
 * so a page pays for ONE request however many callers ask.
 *
 * Pass `relationships: undefined` to switch it off entirely — which is what a
 * quick-create dialog does, since it renders no hint and so can never use the
 * answer.
 */
export function useOtherRecordTypeFieldCount(
  entityType: string,
  relationships: string[] | undefined
): number {
  const enabled = !!RELATIONSHIP_SCOPED_ENTITIES[entityType] && relationships !== undefined;
  const { data } = useQuery({
    queryKey: allCustomFieldDefinitionsKey,
    queryFn: () => listDefinitionsApiV1CustomFieldsDefinitionsGet(),
    enabled,
  });
  return useMemo(
    () => (enabled ? otherRecordTypeFieldCount(data?.definitions, entityType, relationships) : 0),
    [enabled, data?.definitions, entityType, relationships]
  );
}
